import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementFrom,
  type PartialPropsOf,
  type SlotData,
} from '@blaze-runtime/engine-domain';
import {
  type ElementAlignment,
  type ElementHorizontalAlignment,
  type ElementVerticalAlignment,
  splitElementAlignment,
} from '@blaze-shared/elements-domain';

import { HorizontalAlignmentStyle, VerticalAlignmentStyle } from '../../../ai/models/styles';
import {
  type DefinitionMapper,
  type ResolvedChildDefinition,
} from '../../../ai/page/definition-mapper';

import {
  type ColumnWidthMode,
  type ContainerDefinition,
  type ContainerSlot,
} from './container.definition';

const ALLOWED_WIDTH_VALUES = [0.2, 0.25, 0.33, 0.5, 0.6, 0.66, 0.75, 1];

export class ContainerDefinitionMapper implements DefinitionMapper<'container'> {
  readonly type = 'container';

  assembleChildren(
    definition: PartialDeep<ContainerDefinition>,
    slotChildren: ResolvedChildDefinition[][],
  ): void {
    definition.children ??= slotChildren.map(() => ({ $type: 'column', children: [] }));

    slotChildren.forEach((children, index) => {
      const defSlot = definition.children?.[index];
      if (defSlot) {
        defSlot.children = children as unknown as ContainerSlot['children'];
      }
    });
  }

  extract(element: CanvasElementFrom<'container'>): PartialDeep<ContainerDefinition> {
    const slots = element.slots;

    const children: ContainerSlot[] = [];
    if (slots && slots.ids) {
      slots.ids.forEach((id) => {
        const slot: SlotData<PartialPropsOf<'container'>> | undefined = slots[id];
        const widthMode = this.getWidthMode(slot);
        children.push({
          $type: 'column',
          widthMode,
          widthPercent: widthMode === 'Percent' ? this.getWidthPercent(slot) : undefined,
          verticalAlignment: getVerticalAlignmentStyle(slot?.alignment?.vertical),
          horizontalAlignment: getHorizontalAlignmentStyle(slot?.alignment?.horizontal),
          styleName: slot?.props?.styleConfig?.styleName,
          children: [],
        });
      });
    }

    const minimumHeight =
      element.height?.mode === 'value' && typeof element.height.value === 'number'
        ? element.height.value
        : undefined;

    const rowAlignment = this.getRowAlignment(element.props.style?.container?.offset?.align);

    return {
      children: children,
      minimumHeight: minimumHeight,
      ...rowAlignment,
      styleName: element.props.styleConfig?.styleName,
    };
  }

  private getWidthMode(slot: SlotData<PartialPropsOf<'container'>> | undefined): ColumnWidthMode {
    return slot?.sizing?.mode === 'fit-content' ? 'FitContent' : 'Percent';
  }

  private getWidthPercent(
    slot: SlotData<PartialPropsOf<'container'>> | undefined,
  ): number | undefined {
    if (slot?.sizing?.mode === 'percent' && typeof slot.sizing.width === 'number') {
      const width = +(slot.sizing.width / 100).toFixed(2);
      return this.getAllowedWidthValue(width);
    }

    return undefined;
  }

  private getAllowedWidthValue(width: number): number {
    return ALLOWED_WIDTH_VALUES.reduce((prev, curr) => {
      return Math.abs(curr - width) < Math.abs(prev - width) ? curr : prev;
    }, Infinity);
  }

  private getRowAlignment(align: ElementAlignment | undefined): {
    verticalAlignment?: VerticalAlignmentStyle;
    horizontalAlignment?: HorizontalAlignmentStyle;
  } {
    if (!align) {
      return {};
    }

    const [vertical, horizontal] = splitElementAlignment(align);
    const verticalAlignment = getVerticalAlignmentStyle(vertical);
    const horizontalAlignment = getHorizontalAlignmentStyle(horizontal);
    return { verticalAlignment, horizontalAlignment };
  }
}

function getVerticalAlignmentStyle(
  verticalAlignment: ElementVerticalAlignment | undefined,
): VerticalAlignmentStyle | undefined {
  switch (verticalAlignment) {
    case 'top':
      return VerticalAlignmentStyle.Top;
    case 'middle':
      return VerticalAlignmentStyle.Center;
    case 'bottom':
      return VerticalAlignmentStyle.Bottom;
  }

  return undefined;
}

function getHorizontalAlignmentStyle(
  horizontalAlignment: ElementHorizontalAlignment | undefined,
): HorizontalAlignmentStyle | undefined {
  switch (horizontalAlignment) {
    case 'left':
      return HorizontalAlignmentStyle.Left;
    case 'center':
      return HorizontalAlignmentStyle.Center;
    case 'right':
      return HorizontalAlignmentStyle.Right;
  }

  return undefined;
}
